import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

// Define the content for the multi-step text module
const moduleContent: ModuleSection[] = [
  {
    title: 'Why Connectedness Matters to You',
    content: [
      '<strong>Connectedness</strong> is the awareness that you <strong>belong</strong>, period.',
      'When you feel <strong>isolated</strong>, it\'s often because your mind is telling you you\'re a single unit that has to fight every battle alone. This leads to <strong>loneliness, apathy, and isolation</strong>. This module teaches you to stop viewing life as a battlefield and start seeing it as a <strong>network of relationships</strong>–with your family, friends, community, and the entire natural world.',
      'This isn\'t just a nice idea; it\'s a <strong>profound feeling</strong> that brings <strong>spiritual well-being</strong> because it gives your life context and meaning beyond just your own struggles.'
    ],
  },
  {
    title: 'How You Can Start Feeling More Connected',
    content: [
      'The shift happens not by finding new people, but by changing how you relate to what\'s already around you.'
    ],
    additionalSection: {
      title: 'Strengthen Your Bonds',
      content: [
        'This means making your relationships richer and more real, focusing on three areas:',
        '• <strong>Social & Emotional:</strong> Move past small talk. Practice <strong>authenticity</strong> by letting people see the real you, flaws and all. True connection requires being <strong>genuinely present and vulnerable</strong>.',
        '• <strong>Spiritual:</strong> This bond is your awareness of being connected to something bigger–the universe, nature, or a higher power. It grows every time you feel <strong>awe or gratitude</strong> for being alive.',
      ]
    },
    additionalSection2: {
      title: 'See the World as Interdependent',
      content: [
        'These three elements are the <strong>active muscles</strong> of connection.',
        '• <strong>Empathy:</strong> Realize that the struggles you face–stress, fear, doubt–are shared by everyone. This realization automatically reduces isolation. When you understand others, you understand yourself better.',
        '• <strong>Service & Kindness:</strong> Making an effort for someone else (even a tiny act of kindness) shifts your focus outward. This is a powerful <strong>antidote to apathy</strong>, as giving helps you feel needed and valuable–it\'s a <strong>spiritual practice</strong> because the positive energy immediately returns to you as a sense of purpose.',
      ]
    }
  },
  {
    title: 'Simple Tools to Practice Belonging',
    content: [
      'You don\'t need to quit your job and move to a monastery. These small actions ground you in connection every day:',
      '• <strong>Community Mapping:</strong> Simply write down the names of people, places, or groups that consistently make you feel <strong>energized, safe, or happy</strong>. This visually reminds you that <strong>support is already present</strong> in your life.',
      '• <strong>Nature Reflection:</strong> Spend just 10 minutes outside, paying attention–watch a bug, listen to the wind, feel the sun. Focus on being a <strong>part of that scene</strong>, not just an observer. This is a non-verbal, direct way to feel connected to the flow of life.',
      '• <strong>Service Action:</strong> Make a tiny, intentional act of kindness your <strong>spiritual homework</strong> today. Pay a compliment, hold a door, or donate an old coat. It immediately shifts you into the role of <strong>contributor</strong>.',
      '<br/>',
      '<strong>Key Message:</strong> The core truth is simple: Connection grows when we see others not as separate, but as mirrors of our shared humanity. When you look past differences and see the common human experience, you rediscover your fundamental sense of belonging.'
    ],
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed the module content! Now, let\'s reinforce your learning with a quick quiz to check your understanding.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about connectedness and building relationships.',
        'The quiz will help you review key concepts about feeling connected to others, nature, and finding your place in the larger network of life.'
      ]
    }
  },
];

// Main component, matching the design of the standard assessment form
const SpiritualModule2Page = () => {
  const navigate = useNavigate();
  const { currentStep: rawCurrentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('spiritualM2');
  const totalSteps = moduleContent.length;

  // Ensure currentStep is within valid bounds
  const currentStep = Math.min(rawCurrentStep, totalSteps - 1);

  const currentModule = moduleContent[currentStep];
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep);
    } else {
      // Mark the module as completed and navigate to quiz
      await saveProgress(currentStep, true);
      navigate('/panel/learn/spiritual-module-2/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/spiritual-resources'); // Exit module if on first step
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep);
  };

  const isCompleted = currentStep === totalSteps - 1;

  return (
    // 1. Updated wrapper class: module-form-page -> module-form
    <div className="module-form">
      <div className="module-container">
        
        {/* Left Panel - Illustration */}
        {/* 2. Updated left panel class: module-left-panel -> module-left */}
        <div 
          className="module-left spiritual-panel"
        >
          {/* 3. Retaining module-header for left panel content */}
          <div className="module-header">
            <h1>Module 2</h1>
            <p className="module-subtitle">Connectedness – Building Relationship with Life</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
            Connectedness is the foundation of spiritual well-being. It's the awareness that we are part of something larger – a network of relationships that includes people, nature, and even the unseen.
            </p>            
            <p className="module-tool-info">
            When people feel disconnected, they often experience loneliness, apathy, or isolation.
            </p>
          </div>
        </div>

        {/* Right Panel - Content */}
        {/* 4. Updated right panel class: module-right-panel -> module-right */}
        <div className="module-right">
          
          {/* Content Wrapper for flex space-between layout */}
          <div className="module-content"> 
          
            {/* Progress Bar */}
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            {/* Content Section */}
            <div className="module-section">
              <h2 className="module-title"> 
                {` ${currentModule.title}`}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {/* Additional Title Sections */}
            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              // Logic moved to handleBack, so button is never disabled until the end state is reached.
              disabled={false} 
            >
              {currentStep === 0 ? 'Exit Module' : 'Back'}
            </button>
            
            <button
              className="btn-primary"
              onClick={handleNext}
            >
              {isCompleted ? 'Take the Quiz' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpiritualModule2Page;

